import { Intent } from "@blueprintjs/core";
import { addMinutes, format, parseJSON } from "date-fns";
import {
  ButtonContainer,
  DataTable,
  FGListen,
  FGTextAreaInput,
  FieldGroup,
  IDataTableColumn,
  showError,
  useGridState
} from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";
import * as Yup from "yup";

import { DossierAdmissionDocumentEditDialog, DossierAdmissionDocumentUploadDialog } from ".";
import {
  AcquisAdmissionApi,
  DossierAdmissionEditDtoFromJSON,
  DossierAdmissionFichierGridDto
} from "../../../../../../api";
import {
  AddButton,
  DeleteButton,
  DownloadButton,
  EditButton,
  FGWalterDateMaskInput,
  FGWalterSelectInput,
  SmallFormGenerator
} from "../../../../../../components";
import { useDialog } from "../../../../../../contexts";
import { useApiService, useCrudApi, useTl } from "../../../../../../hooks";
import { useReferential } from "../../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../../locales";
import { exportFile } from "../../../../../../utils";

export interface IDossierAdmissionDetailProps {
  onTypeAcquisChange?: (typeAcquis: string) => void;
  idpersonne: number;
  listRoute: string;
}

export const DossierAdmissionDetail: React.FunctionComponent<IDossierAdmissionDetailProps> = ({
  onTypeAcquisChange,
  idpersonne,
  listRoute
}) => {
  const { t } = useTl();
  const api = useApiService(AcquisAdmissionApi);
  const [typeAcquis, taLoading] = useReferential(a => a.referentialGetAcquisAdmissionTypes(), false, []);
  const [anneeFormation, afLoading] = useReferential(a => a.referentialGetAnneeScolaire(), false, []);
  const [resultats, resuLoading] = useReferential(a => a.referentialGetResultatDossierAdmissions(), false, []);
  const [examinateurs, eLoading] = useReferential(a => a.referentialGetCentreGlobalUsers({ currentIduser: 0 }));
  const history = useHistory();
  const { state, acquisId } = useParams<{
    id: string;
    tab: string;
    state: string;
    acquisId: string;
  }>();
  const editMode = React.useMemo(() => state === "edit", [state]);

  const { data, loading, saveItem, saving, deleteItem, deleting, validationErrors, refresh: refreshData } = useCrudApi(
    React.useMemo(
      () => ({
        getApiFn: async () => {
          return +acquisId <= 0
            ? DossierAdmissionEditDtoFromJSON({
                iddossierAdmission: +acquisId,
                idpersonne: idpersonne,
                typeAcquis: "DossierAdmission"
              })
            : api.acquisAdmissionGetDossierAdmission({ id: +acquisId });
        },
        saveApiFn: d => api.acquisAdmissionSaveDossierAdmission({ DossierAdmissionEditDto: d }),
        onSavedRoute: d => listRoute,
        getDeps: [acquisId],
        deleteApiFn: d => api.acquisAdmissionDeleteDossierAdmission({ id: d.iddossierAdmission }),
        onDeletedRoute: () => listRoute
      }),
      [acquisId, idpersonne, api, listRoute]
    )
  );
  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      typeAcquis: Yup.string().required(t(ETLCodes.Required)),
      date: Yup.date().typeError(t(ETLCodes.DateInvalide))
    });
  }, [t]);

  const tableState = useGridState<any>({
    serverMode: false,
    enablePagination: true,
    enableFilter: false,
    availablePageSizes: [15, 25, 50],
    pageSize: 15,
    sortKeys: { uploadDate: "DESC", fileName: "ASC" }
  });

  const { setData } = tableState;

  React.useEffect(() => {
    if (data?.fichiers) {
      setData(data.fichiers);
    }
  }, [data?.fichiers, setData]);

  const [currentDossierAdmissionFichierId, setCurrentDossierAdmissionFichierId] = React.useState(null);
  const [uploadDialoOpen, setUploadDialoOpen] = React.useState(false);
  const [rowLoading, setRowLoading] = React.useState(null);

  const { showDialogPromise } = useDialog();
  const deleteFichier = React.useCallback(
    async (iddossierAdmissionFichier: number) => {
      const result = await showDialogPromise({
        message: t(ETLCodes.DeleteConfirmationMessage)
      });

      if (result === "yes") {
        try {
          setRowLoading(iddossierAdmissionFichier);
          await api.acquisAdmissionDeleteDossierAdmissionFichier({ id: iddossierAdmissionFichier });
          refreshData();
        } catch {
          showError(t(ETLCodes.ErrorWhileDeletingFile));
        } finally {
          setRowLoading(null);
        }
      }
    },
    [api, refreshData, showDialogPromise, t]
  );

  const downloadFichier = React.useCallback(
    async (iddossierAdmissionFichier: number) => {
      try {
        setRowLoading(iddossierAdmissionFichier);
        const file = await api.acquisAdmissionDownloadDossierAdmissionFichier({ id: iddossierAdmissionFichier });
        await exportFile(file);
      } catch {
        showError(t(ETLCodes.ErrorWhileDownloadingFile));
      } finally {
        setRowLoading(null);
      }
    },
    [api, t]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: DossierAdmissionFichierGridDto) => (
          <ButtonContainer>
            <EditButton
              minimal={true}
              onClick={() => setCurrentDossierAdmissionFichierId(row.iddossierAdmissionFichier)}
              loading={rowLoading === row.iddossierAdmissionFichier}
              disabled={!!rowLoading && rowLoading !== row.iddossierAdmissionFichier}
            />
            <DownloadButton
              minimal={true}
              onClick={() => downloadFichier(row.iddossierAdmissionFichier)}
              loading={rowLoading === row.iddossierAdmissionFichier}
              disabled={!!rowLoading && rowLoading !== row.iddossierAdmissionFichier}
            />
            <DeleteButton
              minimal={true}
              onDelete={() => deleteFichier(row.iddossierAdmissionFichier)}
              loading={rowLoading === row.iddossierAdmissionFichier}
              disabled={!!rowLoading && rowLoading !== row.iddossierAdmissionFichier}
            />
          </ButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.NomFichier),
        fieldName: "fileName"
      },
      {
        header: () => t(ETLCodes.DateUpload),
        fieldName: "uploadDate"
      },
      {
        header: () => t(ETLCodes.UtilisateurUpload),
        fieldName: "creationUserName"
      },
      {
        header: () => t(ETLCodes.Type),
        fieldName: "typeAcquisFichier"
      }
    ],
    [deleteFichier, downloadFichier, rowLoading, t]
  );

  const formatDate = React.useCallback((date: Date) => {
    return format(parseJSON(addMinutes(date, date.getTimezoneOffset())), "dd/MM/yyyy HH:mm");
  }, []);

  const onClose = React.useCallback(
    (refresh?: boolean) => {
      setCurrentDossierAdmissionFichierId(null);
      setUploadDialoOpen(false);
      if (refresh) {
        refreshData();
      }
    },
    [refreshData]
  );

  const onAdd = React.useCallback(() => {
    setUploadDialoOpen(true);
  }, []);

  return (
    <SmallFormGenerator
      initialValues={data}
      onSubmit={saveItem}
      showColons
      editMode={editMode}
      minLabelWidth={180}
      labelAlignment="right"
      formatDate="dd/MM/yyyy"
      validationSchema={FormSchema}
      loading={loading}
      onCancel={() => history.push(listRoute)}
      saving={saving}
      showDeleteButton={+acquisId > 0}
      onDelete={deleteItem}
      deleting={deleting}
      validationErrors={validationErrors}
    >
      <FieldGroup columns={2}>
        <FieldGroup
          fieldsetProps={{
            title: t(ETLCodes.DossierAdmission)
          }}
        >
          <FGListen field="typeAcquis" onChanged={onTypeAcquisChange}></FGListen>
          <FGWalterSelectInput
            label={t(ETLCodes.Type)}
            name="typeAcquis"
            items={typeAcquis}
            loading={taLoading}
            readonly={+(acquisId ?? 0) > 0}
          />
          <FGWalterSelectInput
            label={t(ETLCodes.Annee)}
            name="idanneeDeFormation"
            items={anneeFormation}
            loading={afLoading}
          />
          <FGWalterDateMaskInput label={t(ETLCodes.Date)} name="date" />
          <FGWalterSelectInput label={t(ETLCodes.Resultat)} name="idresultat" items={resultats} loading={resuLoading} />
          <FGWalterSelectInput
            label={t(ETLCodes.Examinateur)}
            name="idexaminateur"
            items={examinateurs}
            loading={eLoading}
          />
          <FGTextAreaInput label={t(ETLCodes.Remarque)} name="remarque" />
        </FieldGroup>
      </FieldGroup>
      <FieldGroup
        visible={+acquisId > 0}
        fieldsetProps={{
          title: t(ETLCodes.Documents),
          rightElement: (
            <AddButton
              onClick={e => {
                e.stopPropagation();
                onAdd();
              }}
              text={t(ETLCodes.General_Add)}
              intent={Intent.PRIMARY}
            />
          )
        }}
      >
        <DataTable
          dateFormat="dd/MM/yyyy HH:mm"
          formatDate={formatDate}
          tableState={tableState}
          loading={loading}
          columns={columns}
        ></DataTable>
        {uploadDialoOpen && (
          <DossierAdmissionDocumentUploadDialog
            onClose={onClose}
            dialogOpen={uploadDialoOpen}
            iddossierAdmission={+acquisId}
          />
        )}
        {!!currentDossierAdmissionFichierId && (
          <DossierAdmissionDocumentEditDialog
            onClose={onClose}
            iddossierAdmissionFichier={currentDossierAdmissionFichierId}
          />
        )}
      </FieldGroup>
    </SmallFormGenerator>
  );
};
